import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type GroupBy,
  type HoldingDto,
  type HoldingsResponse,
  type SipView,
  type SortBy,
} from '../lib/api.js';
import { MoneyText, formatCompact, formatExact } from '../components/MoneyText.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';
import { StatementChart } from '../components/StatementChart.js';
import { MenuSelect } from '../components/MenuSelect.js';
import { CountUp } from '../components/CountUp.js';
import { DayGain } from '../components/DayGain.js';
import type { Money, Valuation } from '../lib/api.js';
import { AddSheet } from '../components/AddSheet.js';
import { PrimaryButton } from '../components/Sheet.js';

/**
 * Wealth root (docs/SCREEN-SPECS.md §2): ONE continuous grouped list, sections
 * per asset class in descending family weight — never a class-picker screen
 * (Navigation Law 2 depends on this flatness).
 *
 * Presentation lives in styles/app.css; this file decides only WHAT to show.
 */
interface ManualSummary {
  deposits: {
    id: string; bank: string; member: { name: string }; maturityDate: string;
    ratePercent: number; status: string; daysToMaturity: number;
    valuation: { money: { amount: number; currency: 'INR' }; asOf: string; isEstimate: boolean };
    maturityValue: { amount: number; currency: 'INR' };
  }[];
  policies: {
    id: string; insurer: string; kind: string; subtype: string | null;
    owner: { name: string }; countsTowardNetWorth: boolean;
    sumAssured: { amount: number; currency: 'INR' };
    surrenderValue: { money: { amount: number; currency: 'INR' }; asOf: string; isEstimate: boolean } | null;
  }[];
  netWorthContribution: { money: { amount: number; currency: 'INR' }; asOf: string; isEstimate: boolean };
  coverInForce: { amount: number; currency: 'INR' };
}

const CLASS_LABEL: Record<string, string> = { mf: 'Mutual funds', equity: 'Stocks', epf: 'EPF' };

/**
 * Section headings when grouping by cap or house are the values themselves —
 * AMFI's own words. Only the catch-all needs translating: "unclassified" is a
 * database token, and what the reader needs to know is WHY a row is there.
 */
export function classLabel(klass: string, groupBy: GroupBy): string {
  if (klass === 'unclassified') return groupBy === 'house' ? 'Other holdings' : 'Not classified';
  return CLASS_LABEL[klass] ?? klass;
}

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'type', label: 'Type' },
  { value: 'cap', label: 'Cap / class' },
  { value: 'house', label: 'Fund house' },
];

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'value', label: 'Value' },
  { value: 'cap', label: 'Cap / class' },
  { value: 'house', label: 'Fund house' },
  { value: 'units', label: 'Units' },
  { value: 'confirmed', label: 'Least recently checked' },
];

/**
 * What the second line under each value SAYS.
 *
 * One list answers several different questions — "how much have I made?",
 * "what moved today?", "what did this cost me?" — and they compete for the
 * same few pixels beneath the value. Rather than cram all of them in (the row
 * becomes a spreadsheet) or pick one for everybody (the others become
 * unavailable), the reader chooses, and the choice rides in the URL like every
 * other cut of this list.
 *
 * Annualised return is deliberately ABSENT: a true XIRR needs every cash flow
 * on the folio, which the list DTO does not carry and 69 holdings cannot
 * afford to fetch. It lives in the expanded panel, where the transactions are
 * already loaded. A CAGR guessed from first-seen date would be a plausible
 * WRONG number, and this product does not print those.
 */
export type ViewBy = 'gain' | 'day' | 'pct' | 'invested' | 'value';

const VIEW_OPTIONS: { value: ViewBy; label: string }[] = [
  { value: 'gain', label: 'Profit ₹' },
  { value: 'pct', label: 'Profit %' },
  { value: 'day', label: 'Today’s move' },
  { value: 'invested', label: 'Invested' },
  { value: 'value', label: 'Value only' },
];

/**
 * The group / sort controls — uniform menu-buttons, the toolbar grammar the
 * whole filter row speaks (see MenuSelect). Returned as a fragment so the
 * screen composes them into ONE row with the member filter: three stacked
 * labelled selects read as a settings form, not a toolbar, and were the
 * single most-complained-about surface in the app.
 */
export function ListControls({
  groupBy,
  sortBy,
  viewBy = 'gain',
  onChange,
}: {
  groupBy: GroupBy;
  sortBy: SortBy;
  viewBy?: ViewBy;
  onChange: (patch: Record<string, string | null>) => void;
}) {
  return (
    <>
      <MenuSelect
        label="Group"
        value={groupBy}
        options={GROUP_OPTIONS}
        onChange={(v) => onChange({ group: v === 'type' ? null : v, class: null })}
      />
      <MenuSelect
        label="Sort"
        value={sortBy}
        options={SORT_OPTIONS}
        onChange={(v) => onChange({ sort: v === 'name' ? null : v })}
      />
      <MenuSelect
        label="Show"
        value={viewBy}
        options={VIEW_OPTIONS}
        onChange={(v) => onChange({ view: v === 'gain' ? null : v })}
      />
    </>
  );
}

/**
 * Change one URL parameter, keep the rest.
 *
 * The member and class filters compose — "Ritu's stocks" is a question worth
 * asking — so setting one must never clear the other, which `setParams({k: v})`
 * would do.
 */
function keep(params: URLSearchParams, patch: Record<string, string | null>): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) next.delete(k);
    else next.set(k, v);
  }
  return next;
}

export function Wealth() {
  const [adding, setAdding] = useState(false);
  const queryClient = useQueryClient();
  // The filter lives in the URL, not in state: it survives a refresh, it can be
  // linked to from the Family screen, and back returns to the unfiltered list
  // (Navigation Law 3 — the URL carries the lineage).
  const [params, setParams] = useSearchParams();
  const member = params.get('member');
  const klass = params.get('class');
  // Closed folios are worth nothing, so showing them changes no total — only
  // how much noise sits between the holdings that matter.
  const showClosed = params.get('closed') === 'show';
  /*
   * Grouping and sorting live in the URL for the same reasons the filters do:
   * "the family's small-cap exposure" is a link worth sending someone, and a
   * refresh should not throw the view away. The server does the work — sections
   * arrive render-ready (docs/API-CONTRACT.md §3).
   */
  const groupBy = (params.get('group') as GroupBy | null) ?? 'type';
  // Name by default: the family reconciles this list against an alphabetical
  // broker export, and a value ordering made every cross-check a hunt.
  const sortBy = (params.get('sort') as SortBy | null) ?? 'name';
  // Which figure the rows carry under their value. Profit by default — the
  // question this list was built to answer before today's move existed.
  const viewBy = (params.get('view') as ViewBy | null) ?? 'gain';
  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ['holdings', member, showClosed, groupBy, sortBy],
    /*
     * Filter taps must answer INSTANTLY. Without this, every chip or sort
     * change unmounted the list into a skeleton while the server answered —
     * which reads as "the button did nothing". The previous list stays up,
     * dimmed, and the new one slides in when it arrives.
     */
    placeholderData: (prev) => prev,
    queryFn: () => {
      const query = new URLSearchParams();
      if (member) query.set('member', member);
      if (showClosed) query.set('closed', 'show');
      if (groupBy !== 'type') query.set('group', groupBy);
      if (sortBy !== 'name') query.set('sort', sortBy);
      const suffix = query.toString();
      return api.get<HoldingsResponse>(`/holdings${suffix ? `?${suffix}` : ''}`);
    },
  });
  // Manual assets are a separate read model but the same question — "what do we
  // own?" — so they render in the same list rather than on a screen of their own.
  const manual = useQuery({ queryKey: ['manual'], queryFn: () => api.get<ManualSummary>('/manual') });

  /*
   * Fetch today's NAVs and closing prices on demand. The daily job is the
   * normal path; this exists for the moment someone is looking at a red dot and
   * wants it resolved. The server throttles it, so a repeated tap cannot become
   * a stream of requests to AMFI and the exchanges.
   */
  const refresh = useMutation({
    mutationFn: () => api.post<{ refreshed: boolean; retryInSeconds?: number }>('/prices/refresh'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['holdings'] }),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  /*
   * The tab in the URL, or the class holding the most money. Defaulting to the
   * largest means the screen opens on what the family mostly owns rather than
   * on whichever class sorted first.
   */
  const activeClass =
    data.sections.find((s) => s.class === klass)?.class ?? data.sections[0]?.class ?? 'mf';

  // Counted across the WHOLE portfolio, not the visible tab: a stale price on
  // another tab is still a stale price, and hiding the prompt would hide it.
  const staleCount = data.sections
    .flatMap((s) => s.holdings)
    .filter((h) => h.price.stale).length;

  return (
    <div className={isFetching && !isPending ? 'is-refetching' : undefined}>
      <div className="pagehead">
        <h1 className="page-title">Wealth</h1>
        {staleCount > 0 && (
          /* The header's one action — riding the title row, not floating
             mid-page like an error scrap. */
          <button
            className="btn btn-sm"
            style={{ color: 'var(--color-value-negative)' }}
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? 'Refreshing…' : `↻ ${staleCount} stale`}
          </button>
        )}
      </div>

      {/* The hero: one panel with presence — soft brand glow, counted-up
          number, provenance on the same baseline. The screen's wow lives here
          and ONLY here; everything below stays quiet so this can be loud. */}
      <div className="hero-panel">
        <span className="eyebrow">Total value</span>
        <div className="metric-line">
          <span className="hero-money">
            <CountUp paise={data.total.money.amount} />
          </span>
          {/* Today's move rides the hero baseline: it is the first thing an
              investor looks for, and it is ABOUT the number beside it — the
              portfolio the current filter shows, not some other total. */}
          <DayGain money={data.dayGain} size="chip" />
          <span className="page-sub" style={{ margin: 0 }}>
            as of {data.total.asOf.slice(0, 10)}
            {data.hiddenCount > 0 && ` · ${data.hiddenCount} hidden`}
            {data.duplicates.count > 0 && ` · ${data.duplicates.count} counted once`}
          </span>
        </div>
      </div>

      {/*
        ONE toolbar, one grammar: Member, Group and Sort are the same
        menu-button on every form factor. The member options carry each
        person's total as right-aligned meta — the comparability the old chip
        row bought with two screen-rows of pills.
      */}
      <div className="toolbar">
        {data.members.length > 1 && (
          <MenuSelect
            label="Member"
            value={member ?? ''}
            options={[
              { value: '', label: 'Everyone', meta: formatCompact(data.total.money.amount) },
              ...data.members.map((m) => ({
                value: m.id,
                label: m.name,
                meta: formatCompact(m.total.money.amount),
              })),
            ]}
            onChange={(v) => setParams(keep(params, { member: v || null }))}
          />
        )}
        <ListControls
          groupBy={groupBy}
          sortBy={sortBy}
          viewBy={viewBy}
          onChange={(patch) => setParams(keep(params, patch))}
        />
      </div>

      {/*
        ONE asset class at a time. Funds and shares answer different questions
        and are priced by different sources, so stacking every table on one
        screen made the page a scroll rather than an answer. The tab bar still
        names every class the family owns, with its subtotal, so choosing one
        never hides that the others exist.
      */}
      {data.sections.length > 1 && (
        <div
          className="tabstrip"
          role="tablist"
          aria-label={groupBy === 'type' ? 'Asset class' : groupBy === 'cap' ? 'Cap or class' : 'Fund house'}
        >
          {data.sections.map((s) => (
            <button
              key={s.class}
              role="tab"
              aria-selected={activeClass === s.class}
              className="tabstrip-tab"
              onClick={() => setParams(keep(params, { class: s.class }))}
            >
              {classLabel(s.class, groupBy)}
              <span className="tabstrip-value">{formatCompact(s.subtotal.money.amount)}</span>
            </button>
          ))}
        </div>
      )}

      {data.sections
        .filter((s) => s.class === activeClass)
        .map((section) => (
          <Card
            key={section.class}
            title={
              <>
                <span>
                  {section.holdings.length} holding{section.holdings.length === 1 ? '' : 's'} —{' '}
                  {formatCompact(section.subtotal.money.amount)}
                </span>
                {/* This class's own move, on the header that owns it: "did my
                    funds move, or was it the shares?" is answered without
                    reading a single row. */}
                <DayGain money={section.dayGain} size="inline" />
              </>
            }
          >
            {section.holdings.map((h) => (
              <HoldingRow
                key={h.id}
                holding={h}
                viewBy={viewBy}
                open={params.get('open') === h.id}
                onToggle={() =>
                  setParams(keep(params, { open: params.get('open') === h.id ? null : h.id }), {
                    replace: true,
                  })
                }
              />
            ))}
          </Card>
        ))}

      {manual.data && manual.data.deposits.length > 0 && (
        <Card title={`Fixed deposits — ${formatCompact(manual.data.netWorthContribution.money.amount)}`}>
          {manual.data.deposits.map((d) => (
            <Row
              key={d.id}
              left={
                <span>
                  <span className="row-title">{d.bank}</span>
                  <span className="row-meta">
                    {d.member.name} · {d.ratePercent}% · matures {d.maturityDate}
                    {d.daysToMaturity <= 30 && d.daysToMaturity >= 0 && (
                      <span className="state-warn"> · ⚠ {d.daysToMaturity}d</span>
                    )}
                  </span>
                </span>
              }
              right={<MoneyText value={d.valuation} />}
            />
          ))}
        </Card>
      )}

      {/*
        Protection is listed, never summed into wealth. Showing cover in force
        beside a total that deliberately excludes it is the clearest way to make
        that distinction visible rather than merely true.
      */}
      {manual.data && manual.data.policies.length > 0 && (
        <Card title={`Protection — ${formatCompact(manual.data.coverInForce.amount)} cover`}>
          {manual.data.policies.map((p) => (
            <Row
              key={p.id}
              left={
                <span>
                  <span className="row-title">{p.insurer}</span>
                  <span className="row-meta">
                    {p.owner.name} · {p.subtype ?? p.kind}
                  </span>
                </span>
              }
              right={
                p.surrenderValue ? (
                  <MoneyText value={p.surrenderValue} />
                ) : (
                  <span className="badge badge-muted">not wealth</span>
                )
              }
            />
          ))}
        </Card>
      )}

      {/* The screen's one verb, in the thumb zone (Law 1 + ergonomics law). */}
      <PrimaryButton onClick={() => setAdding(true)} style={{ marginTop: 'var(--space-4)' }}>
        Add to your wealth
      </PrimaryButton>
      {adding && <AddSheet onClose={() => setAdding(false)} />}
    </div>
  );
}

/** True when this folio saw a transaction in the month the viewer is in. */
function movedThisMonth(h: HoldingDto): boolean {
  if (!h.lastTransactionAt) return false;
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return h.lastTransactionAt.startsWith(ym);
}

/**
 * One holding — a clean summary row that EXPANDS IN PLACE.
 *
 * The old pattern navigated to a detail page and back, which threw away scroll
 * position and list state on every look — poison for the reconciliation pass
 * this list exists for. Tap now opens the detail under the row: chart, facts,
 * transactions and the confirm/SIP controls, lazily fetched and cached. The
 * open row lives in the URL (?open=…, replace-history so Back still leaves the
 * page, not the row), and the full-page view survives for deep links.
 *
 * The collapsed row keeps only the at-a-scroll signals: price dot, name,
 * current-month badge, check-state glyph, units, value and gain. Everything
 * actionable moved into the panel — a row wearing two inline forms was noise.
 */
function HoldingRow({
  holding: h,
  viewBy,
  open,
  onToggle,
}: {
  holding: HoldingDto;
  viewBy: ViewBy;
  open: boolean;
  onToggle: () => void;
}) {
  const gain = h.gain;
  const up = (gain?.money.amount ?? 0) >= 0;
  const active = movedThisMonth(h);
  const c = h.confirmation;
  const checkState = c === null ? 'state-bad' : c.agrees ? 'state-good' : 'state-warn';
  const checkGlyph = c === null ? '✗' : c.agrees ? '✓' : '⚠';

  /*
   * A row opened from elsewhere (the ⌘K palette lands on /wealth?open=…) must
   * bring itself into view — the row was expanding correctly below the fold,
   * which reads as "nothing happened". 'nearest' keeps manual toggles from
   * jumping a row that is already visible.
   */
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [open]);

  return (
    <div ref={rowRef} className={`row${active ? ' row-accent' : ''}`}>
      <button
        type="button"
        className="row-main rowbtn"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="row-left">
          <span className="row-title row-title-split">
            <span aria-hidden className={`dot ${h.price.stale ? 'dot-bad' : 'dot-good'}`} />
            <span className="row-name">{h.scheme.name || h.scheme.isin}</span>
            {h.lastTransactionAt && (
              /*
               * Money movement is ALWAYS reflected on the row, not only in the
               * month it happened: tinted while current, quiet grey once it
               * ages. A fund with 32 transactions must never look inert.
               */
              <span
                className={`badge ${active ? 'badge-tint' : 'badge-muted'}`}
                style={{ marginLeft: 'var(--space-2)' }}
              >
                txn {h.lastTransactionAt.slice(8, 10)}/{h.lastTransactionAt.slice(5, 7)}
              </span>
            )}
          </span>
          <span className="row-meta">
            {h.member.name} · {h.folio}
            {h.units !== null &&
              ` · ${h.units.toLocaleString('en-IN', { maximumFractionDigits: 3 })} units`}
            {(() => {
              /*
               * The running commitment, on the list itself: a declared plan
               * first, else the detected one. "↻ ₹15,000/mo" answers the
               * question the family actually scans this list with — what is
               * still going out every month — without opening anything.
               */
              const plan = h.sip?.declared?.active
                ? h.sip.declared
                : h.sip?.detected?.status === 'active'
                  ? h.sip.detected
                  : null;
              return plan ? (
                <span className="state-muted">
                  {' '}
                  · ↻ {formatCompact(plan.amount.amount)}/{plan.cadence === 'quarterly' ? 'qtr' : 'mo'}
                </span>
              ) : null;
            })()}
            {/* Reconciliation state stays visible at a scroll (north-star rule 10). */}
            {h.scheme.type !== 'epf' && (
              <span className={checkState}>
                {' '}
                {checkGlyph} {c === null ? 'unchecked' : c.agrees ? 'checked' : 'differs'}
              </span>
            )}
            {h.member.unmatched && <span className="state-warn"> · unmatched</span>}
            {h.status === 'pending_review' && <span className="state-warn"> · ⚠ review</span>}
          </span>
        </span>
        <span className="row-right">
          {h.valuation ? (
            <span className="money-cell">
              <MoneyText value={h.valuation} />
            </span>
          ) : (
            <span className="state-muted">—</span>
          )}
          {/*
            The second line answers whichever question the Show menu is set to.
            Each branch renders only what it can KNOW — a missing cost basis or
            a missing price pair yields nothing rather than a zero, so a blank
            here always means "not known", never "no change".
          */}
          {viewBy === 'gain' && gain && (
            <span className={`row-detail ${up ? 'delta-pos' : 'delta-neg'}`}>
              {up ? '+' : '−'}
              {formatCompact(Math.abs(gain.money.amount))}
              {gain.percent !== null && ` · ${up ? '+' : '−'}${Math.abs(gain.percent).toFixed(1)}%`}
            </span>
          )}
          {viewBy === 'pct' && gain && gain.percent !== null && (
            <span className={`row-detail ${up ? 'delta-pos' : 'delta-neg'}`}>
              {up ? '+' : '−'}
              {Math.abs(gain.percent).toFixed(1)}%
            </span>
          )}
          {viewBy === 'day' && (
            <span className="row-detail">
              <DayGain money={h.dayGain?.money ?? null} percent={h.dayGain?.percent ?? null} />
            </span>
          )}
          {viewBy === 'invested' && (
            <span className="row-detail state-muted">
              {h.invested ? `${formatCompact(h.invested.amount)} in` : '—'}
            </span>
          )}
          <span className={`chevron${open ? ' chevron-open' : ''}`} aria-hidden>
            ›
          </span>
        </span>
      </button>
      <div className={`expand${open ? ' expand-open' : ''}`}>
        <div>{open && <HoldingPanel holding={h} />}</div>
      </div>
    </div>
  );
}

/** The lazily-fetched detail for one expanded row. */
interface PanelDto {
  nominees: string[] | null;
  lastSeen: string | null;
  folioFull: string;
  invested: Money | null;
  xirrPercent: number | null;
  valuation: Valuation | null;
  history: { asOf: string; units: number | null; value: Money; invested: Money | null }[];
  transactions: {
    date: string;
    type: string;
    units: number | null;
    amount: Money;
    nav: Money | null;
    description: string | null;
  }[];
}

function HoldingPanel({ holding: h }: { holding: HoldingDto }) {
  const { data, isPending, error } = useQuery({
    // Same key as the full-page view, so the two share one cache entry.
    queryKey: ['holding', h.id],
    queryFn: () => api.get<PanelDto>(`/holdings/${h.id}`),
  });

  if (isPending) return <div className="skeleton-block" style={{ height: 140 }} />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const chartPoints = data.history.map((pt) => ({ asOf: pt.asOf, value: pt.value, invested: pt.invested }));

  return (
    <div className="expand-body">
      {/*
        The chart draws only what was measured: one dot per statement, the
        dashed line is invested cost, and the space between them is the gain.
        A single observation gets a fact, not a one-point plot.
      */}
      {chartPoints.length > 1 ? (
        <StatementChart points={chartPoints} />
      ) : (
        <p className="page-sub" style={{ margin: 0 }}>
          Only one statement so far — the chart appears when there is change to draw.
        </p>
      )}

      <div className="facts">
        <span>
          <span className="fact-label">Invested</span>
          <span className="fact-value">{data.invested ? formatCompact(data.invested.amount) : '—'}</span>
        </span>
        <span>
          <span className="fact-label">Return (XIRR)</span>
          <span className={`fact-value ${data.xirrPercent === null ? '' : data.xirrPercent >= 0 ? 'delta-pos' : 'delta-neg'}`}>
            {data.xirrPercent === null ? '—' : `${data.xirrPercent >= 0 ? '▲' : '▼'} ${Math.abs(data.xirrPercent).toFixed(1)}%`}
          </span>
        </span>
        <span>
          <span className="fact-label">Nominee</span>
          <span className="fact-value">
            {data.nominees === null ? (
              <span className="state-muted">Not stated</span>
            ) : data.nominees.length === 0 ? (
              <span className="state-warn">None registered</span>
            ) : (
              data.nominees.join(', ')
            )}
          </span>
        </span>
        <span>
          <span className="fact-label">Last confirmed</span>
          <span className="fact-value">{data.lastSeen ? data.lastSeen.slice(0, 10) : '—'}</span>
        </span>
        <span>
          <span className="fact-label">Priced</span>
          <span className={`fact-value ${h.price.stale ? 'state-bad' : ''}`}>
            {data.valuation ? data.valuation.asOf.slice(0, 10) : '—'}
          </span>
        </span>
      </div>

      {/* The actions live here now, off the collapsed row. */}
      <div style={{ display: 'grid', gap: 2 }}>
        <ConfirmControl holding={h} />
        {h.scheme.type === 'mf' && <SipControl holding={h} />}
      </div>

      {/*
        Transactions on the same page, one disclosure away. A native <details>
        restyled — free keyboard and screen-reader semantics.
      */}
      {data.transactions.length > 0 && (
        <details className="disclosure">
          <summary>
            Transactions <span className="chip-value">{data.transactions.length}</span>
          </summary>
          <div>
            {data.transactions.map((txn, i) => (
              <Row
                key={`${txn.date}-${i}`}
                left={
                  <span>
                    <span style={{ display: 'block', font: 'var(--type-subhead)' }}>
                      {txn.description || txn.type}
                    </span>
                    <span className="row-detail">
                      {txn.date}
                      {txn.units !== null &&
                        ` · ${txn.units.toLocaleString('en-IN', { maximumFractionDigits: 3 })} units`}
                      {txn.nav && ` @ ${formatExact(txn.nav.amount)}`}
                    </span>
                  </span>
                }
                right={<MoneyText value={txn.amount} signed />}
              />
            ))}
          </div>
        </details>
      )}

      <Link to={`/wealth/holding/${h.id}`} className="linkish" style={{ color: 'var(--color-tint)' }}>
        Open full page ↗
      </Link>
    </div>
  );
}

/** "3 days ago" — a confirmation's age is what makes it trustworthy or not. */
export function agoLabel(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

/**
 * Confirm a folio's unit count by hand.
 *
 * Statements lag reality, so the family's own count is often the more current
 * number — but it is a CLAIM, not a priced figure, so it is recorded beside the
 * statement's rather than replacing it (docs/API-CONTRACT.md §3.1). What this
 * control is really for is surfacing the gap: a disagreement almost always
 * means a statement has not arrived yet.
 *
 * EPF is excluded upstream — it has no units to count.
 */
export function ConfirmControl({ holding: h }: { holding: HoldingDto }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Seeded with the statement's figure: confirming that it is already right is
  // the common case, and should be one tap rather than retyping four decimals.
  const [value, setValue] = useState(() => String(h.confirmation?.units ?? h.units ?? ''));

  const save = useMutation({
    mutationFn: (units: number | null) =>
      api.put<HoldingDto>(`/holdings/${h.id}/confirmation`, { units, version: h.version }),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['holdings'] });
    },
  });

  const c = h.confirmation;
  const label = c ? `Checked ${agoLabel(c.at)}` : 'Not checked';
  const disagrees = c !== null && !c.agrees;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        /*
         * Checked-and-agreeing is GREEN, never-checked (or disagreeing) is
         * RED: the list doubles as the reconciliation worklist, and the two
         * states have to be tellable apart at a scroll. Text carries the
         * meaning too — colour alone is not readable to everyone.
         */
        className={`linkish ${disagrees ? 'state-warn' : c ? 'state-good' : 'state-bad'}`}
      >
        {/*
          The disagreement is stated in units, not hidden behind an icon: the
          size of the gap is what tells the reader whether it is rounding or a
          transaction they have forgotten about.
        */}
        {disagrees
          ? `⚠ You counted ${c.units} · statement says ${h.units ?? '—'}`
          : c
            ? `✓ ${label}`
            : `✗ ${label}`}
        <span aria-hidden> ✎</span>
      </button>
    );
  }

  const parsed = value.trim() === '' ? null : Number(value);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed < 0);

  return (
    <div className="toolbar" style={{ marginBottom: 0 }}>
      <label className="field-inline">
        Units{' '}
        <input
          autoFocus
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={`Confirmed units for ${h.scheme.name}, ${h.member.name}, folio ${h.folio}`}
          style={{ width: '11ch' }}
        />
      </label>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={invalid || save.isPending}
        onClick={() => save.mutate(parsed)}
      >
        {save.isPending ? 'Saving…' : 'Confirm'}
      </button>
      {c && (
        <button type="button" className="btn btn-sm" disabled={save.isPending} onClick={() => save.mutate(null)}>
          Clear
        </button>
      )}
      <button type="button" className="btn btn-sm btn-quiet" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {/*
        A stale version means another tab already moved this holding on. Saying
        so beats a silent no-op that looks like success.
      */}
      {save.error && <span className="state-bad" style={{ font: 'var(--type-caption)' }}>{(save.error as Error).message}</span>}
    </div>
  );
}

/** "₹10,000 monthly on the 5th" — the plan in one line. */
export function sipSummary(d: NonNullable<SipView['declared']>): string {
  const amount = `₹${(d.amount.amount / 100).toLocaleString('en-IN')}`;
  const when = d.cadence === 'quarterly' ? 'quarterly' : 'monthly';
  const until = d.endDate === null ? 'until cancelled' : `to ${d.endDate}`;
  return `${amount} ${when} on the ${d.dayOfMonth}${ordinal(d.dayOfMonth)} · ${until}`;
}

function ordinal(n: number): string {
  if (n >= 11 && n <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
}

/**
 * Declare the SIP set up on a fund.
 *
 * The app already DETECTS instalment plans from transaction rhythm; this is the
 * other half — what the family knows they set up. Both are shown and neither
 * overwrites the other, because the disagreement between them is the useful
 * part: a declared plan with no recent instalment means a bounced payment, a
 * moved debit date, or a statement that has not arrived.
 *
 * Mutual funds only — shares are bought, not subscribed to.
 */
export function SipControl({ holding: h }: { holding: HoldingDto }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const declared = h.sip?.declared ?? null;
  const detected = h.sip?.detected ?? null;

  // Seeded from the declaration, or from what was DETECTED when there is no
  // declaration yet — confirming a plan the statements already found should be
  // a glance and a tap, not a retyping exercise.
  const [amount, setAmount] = useState(() =>
    String((declared?.amount.amount ?? detected?.amount.amount ?? 0) / 100 || ''),
  );
  const [startDate, setStartDate] = useState(() => declared?.startDate ?? '');
  const [endDate, setEndDate] = useState(() => declared?.endDate ?? '');
  const [cadence, setCadence] = useState<'monthly' | 'quarterly'>(() => declared?.cadence ?? 'monthly');

  const save = useMutation({
    mutationFn: (plan: Record<string, unknown> | null) =>
      api.put<HoldingDto>(`/holdings/${h.id}/sip`, { plan, version: h.version }),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['holdings'] });
    },
  });

  if (!open) {
    const mismatch = h.sip?.mismatch ?? null;
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`linkish ${mismatch ? 'state-warn' : declared ? 'state-muted' : ''}`}
      >
        {declared ? `SIP ${sipSummary(declared)}` : detected ? 'SIP detected — not declared' : 'No SIP'}
        {/*
          The disagreement is spelled out rather than reduced to a warning
          triangle: "nothing since June" and "statements show ₹5,000" are
          different problems needing different fixes.
        */}
        {mismatch && <span style={{ display: 'block' }}>⚠ {mismatch}</span>}
        <span aria-hidden> ✎</span>
      </button>
    );
  }

  const rupees = Number(amount);
  const invalid =
    !Number.isFinite(rupees) ||
    rupees <= 0 ||
    !/^\d{4}-\d{2}-\d{2}$/.test(startDate) ||
    (endDate !== '' && (!/^\d{4}-\d{2}-\d{2}$/.test(endDate) || endDate < startDate));

  return (
    <div className="toolbar" style={{ marginBottom: 0 }}>
      <label className="field-inline">
        Amount ₹{' '}
        <input
          autoFocus
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-label={`SIP amount for ${h.scheme.name}, ${h.member.name}`}
          style={{ width: '9ch' }}
        />
      </label>
      <label className="field-inline">
        Starts{' '}
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          aria-label={`SIP start date for ${h.scheme.name}`}
        />
      </label>
      <label className="field-inline">
        {/* Blank is meaningful here, so it is labelled rather than left to guess. */}
        Ends{' '}
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          aria-label={`SIP end date for ${h.scheme.name}, blank means until cancelled`}
        />
      </label>
      <label className="field-inline">
        Every{' '}
        <select
          value={cadence}
          onChange={(e) => setCadence(e.target.value as 'monthly' | 'quarterly')}
          aria-label={`SIP frequency for ${h.scheme.name}`}
        >
          <option value="monthly">Month</option>
          <option value="quarterly">Quarter</option>
        </select>
      </label>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={invalid || save.isPending}
        onClick={() =>
          save.mutate({
            amountPaise: Math.round(rupees * 100),
            startDate,
            endDate: endDate === '' ? null : endDate,
            cadence,
          })
        }
      >
        {save.isPending ? 'Saving…' : 'Save SIP'}
      </button>
      {declared && (
        <button type="button" className="btn btn-sm" disabled={save.isPending} onClick={() => save.mutate(null)}>
          Remove
        </button>
      )}
      <button type="button" className="btn btn-sm btn-quiet" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {save.error && <span className="state-bad" style={{ font: 'var(--type-caption)' }}>{(save.error as Error).message}</span>}
    </div>
  );
}
